import React, { useEffect, useRef, useCallback } from "react";
import BootstrapModal from "bootstrap/js/dist/modal";
import { FaChevronLeft } from "react-icons/fa";
import "./Modal.css";

const Modal = React.forwardRef(
  (
    {
      show,
      onHide,
      title,
      body,
      footer,
      modalName,
      backToPrevious = "",
      handleBackBtn,
      type = "modal-md",
    },
    ref,
  ) => {
    const modalRef = useRef(null);
    const modalInstanceRef = useRef(null);
    const isInitializedRef = useRef(false);
    const combinedRef = ref || modalRef;

    const cleanup = useCallback(() => {
      try {
        const modalElement = combinedRef.current;
        if (modalElement && modalInstanceRef.current) {
          modalElement.removeEventListener("hidden.bs.modal", onHide);
          modalElement.removeEventListener("shown.bs.modal", () => {});

          if (typeof modalInstanceRef.current.dispose === "function") {
            modalInstanceRef.current.dispose();
          }

          modalInstanceRef.current = null;
        }

        document.body.style.paddingRight = "";
        document.body.classList.remove("modal-open");

        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((backdrop) => backdrop.remove());

        isInitializedRef.current = false;
      } catch (error) {
        console.warn("Modal cleanup error handled:", error.message);
      }
    }, [combinedRef, onHide]);

    const showModal = useCallback(() => {
      try {
        const modalElement = combinedRef.current;

        if (!modalElement) {
          console.warn("Modal element not found");
          return;
        }

        // if (!window.bootstrap || !window.bootstrap.Modal) {
        //   console.warn("Bootstrap Modal not available");
        //   return;
        // }

        if (modalInstanceRef.current) {
          cleanup();
        }

        setTimeout(() => {
          try {
            modalInstanceRef.current = new BootstrapModal(modalElement, {
              backdrop: "static",
              keyboard: false,
              focus: false,
            });

            const handleHidden = () => {
              if (typeof onHide === "function") {
                onHide();
              }
              cleanup();
            };

            const handleShown = () => {
              document.body.style.paddingRight = "0px";
            };

            modalElement.addEventListener("hidden.bs.modal", handleHidden, {
              once: true,
            });
            modalElement.addEventListener("shown.bs.modal", handleShown, {
              once: true,
            });

            modalInstanceRef.current.show();
            isInitializedRef.current = true;
          } catch (error) {
            console.warn("Modal show error:", error.message);
            modalElement.style.display = "block";
            modalElement.classList.add("show", "fade");
            modalElement.setAttribute("aria-modal", "true");
            modalElement.setAttribute("role", "dialog");
            document.body.classList.add("modal-open");

            const backdrop = document.createElement("div");
            backdrop.className = "modal-backdrop fade show";
            document.body.appendChild(backdrop);
          }
        }, 50);
      } catch (error) {
        console.warn("Show modal error handled:", error.message);
      }
    }, [combinedRef, cleanup, onHide]);

    const hideModal = useCallback(() => {
      try {
        const modalElement = combinedRef.current;

        if (!modalElement) return;

        if (
          modalInstanceRef.current &&
          typeof modalInstanceRef.current.hide === "function"
        ) {
          modalInstanceRef.current.hide();
        } else {
          modalElement.style.display = "none";
          modalElement.classList.remove("show");
          modalElement.removeAttribute("aria-modal");
          modalElement.removeAttribute("role");
          document.body.classList.remove("modal-open");

          const backdrops = document.querySelectorAll(".modal-backdrop");
          backdrops.forEach((backdrop) => backdrop.remove());

          if (typeof onHide === "function") {
            setTimeout(() => onHide(), 100);
          }
        }
      } catch (error) {
        console.warn("Hide modal error handled:", error.message);
      }
    }, [combinedRef, onHide]);

    useEffect(() => {
      if (show && !isInitializedRef.current) {
        showModal();
      } else if (!show && isInitializedRef.current) {
        hideModal();
      }

      return () => {
        if (isInitializedRef.current) {
          cleanup();
        }
      };
    }, [show, showModal, hideModal, cleanup]);

    useEffect(() => {
      return () => {
        cleanup();
      };
    }, [cleanup]);

    if (!show) return null;

    return (
      <div
        className={`modal fade custom-modal`}
        id={modalName}
        tabIndex="-1"
        aria-labelledby={`${modalName}Label`}
        aria-hidden="true"
        ref={combinedRef}
        data-bs-focus="false"
        style={{ display: "none" }}
      >
        <div
          className={`modal-dialog ${type} modal-dialog-centered modal-dialog-scrollable`}
        >
          <div className="modal-content">
            <div className="modal-header">
              {backToPrevious !== "" ? (
                <button
                  type="button"
                  className="btn back-btn btn-info"
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof handleBackBtn === "function") {
                      handleBackBtn();
                    }
                  }}
                >
                  <FaChevronLeft />
                  {backToPrevious}
                </button>
              ) : null}
              <h1 className="modal-title" id={`${modalName}Label`}>
                {title}
              </h1>
              <button
                type="button"
                className="btn-close"
                onClick={(e) => {
                  e.preventDefault();
                  hideModal();
                }}
                aria-label="Close"
              ></button>
            </div>
            {body && <div className="modal-body">{body}</div>}
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
